import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  ShieldCheck, Lock, Users2, Briefcase, Award, Zap, ChevronRight,
  Building2, CalendarClock, MapPin, UserPlus, Search, HandshakeIcon,
  Video, FileText as FileTextIcon, Sparkles as SparklesIcon, AlertCircle,
  Sparkles,
} from "lucide-react";
import ReadMore from "@/components/shared/ReadMore";
import HomePopup from "@/components/shared/HomePopup";
import { HOME } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { SectionHeader } from "@/components/shared/Common";
import ShareOnWhatsApp from "@/components/shared/ShareOnWhatsApp";

const WA_HOME_MESSAGE = `🛡️ SecuSource — a security workforce platform built by security & CPOs, for the industry.

Looking for security staff, or looking for security work? Ireland & UK.

✅ Free for professionals to join (€0)
✅ You set your own rate — no commission on your pay
✅ Verified guards, CPOs, drivers, supervisors and full cluster teams
✅ One transparent fee for companies — €1/hr per worker OR 3% (whichever is lower)
✅ PSA (IE) + SIA (UK) verified

Sign up here → https://secure-staff-hub.preview.emergentagent.com

Browse teams (no login) → https://secure-staff-hub.preview.emergentagent.com/teams`;

const HERO_IMG = "https://images.unsplash.com/photo-1547882230-87a3d4390e8d?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80";
const CPO_IMG = "https://images.unsplash.com/photo-1618371690240-e0d46eead4b8?crop=entropy&cs=srgb&fm=jpg&w=800&q=80";
const EVENT_IMG = "https://images.unsplash.com/photo-1566245024852-04fbf7842ce9?crop=entropy&cs=srgb&fm=jpg&w=800&q=80";

const helps = [
  { icon: Building2, title: "Security Companies", desc: "Access a national pool of vetted staff and cluster teams.", aud: "company" },
  { icon: Users2, title: "Event Organisers", desc: "Book event security teams with ratings and shared SOPs.", aud: "company" },
  { icon: ShieldCheck, title: "Close Protection", desc: "Search CPOs and VPOs by qualification, kit, and rating.", aud: "company" },
  { icon: Award, title: "Asset Protection", desc: "Reliable static and mobile personnel for high-value sites.", aud: "company" },
  { icon: Zap, title: "Last-Minute Deployments", desc: "Fast-track sourcing when timelines matter most.", aud: "company" },
  { icon: Briefcase, title: "Security Professionals", desc: "Grow your reputation with verified endorsements.", aud: "pro" },
];

const benefits = [
  "Faster staff sourcing",
  "Verified personnel",
  "Anonymous profiles until booking",
  "Team & cluster options",
  "Ratings & endorsements",
  "Mobile-first management",
  "Clear agreements & SOPs",
];

/* Rotating hero headline — cycles through 4 audience-targeted messages */
const HERO_MESSAGES = [
  {
    id: "companies",
    headline: <>Find vetted security professionals and operational teams — <span className="text-brand">faster.</span></>,
    body: "SecuSource is a professional workforce platform — not a recruitment website. Search, verify and book security personnel with clear pricing and full transparency.",
  },
  {
    id: "guards",
    headline: <>Security guards: get seen by hiring companies. <span className="text-brand">Faster work. Fairer pay.</span></>,
    body: "Verified profile visible to every hiring company on the platform. ERO-minimum rates or above only — no undercutting, no race-to-the-bottom.",
  },
  {
    id: "cpo",
    headline: <>CPOs &amp; VPOs — elite close protection work, <span className="text-brand">matched to you.</span></>,
    body: "Search separated for CPOs/VPOs so serious operators don't get mixed with general guarding. Kit, medical & vehicles listed. Book direct, no agency margin.",
  },
  {
    id: "cluster",
    headline: <>Team Leads — build a cluster, deploy as a unit, <span className="text-brand">grow together.</span></>,
    body: "Assemble a cluster team of pros you know and trust. One booking, one contract, one team profile — with rating and endorsements you earn as a unit.",
  },
];

function WhoItHelpsGrid() {
  const { user } = useAuth();
  const filtered = React.useMemo(() => {
    if (!user) return helps;
    if (user.role === "company" || user.role === "super_admin") return helps.filter((h) => h.aud === "company");
    if (user.role === "security_professional" || user.role === "lead_professional") return helps.filter((h) => h.aud === "pro");
    return helps;
  }, [user]);
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="who-it-helps-grid">
      {filtered.map((h, i) => (
        <div key={h.title} className="card-flat p-6 reveal" style={{ animationDelay: `${i * 60}ms` }} data-testid={`who-it-helps-card-${h.aud}`}>
          <div className="w-11 h-11 rounded-md bg-brand-light text-brand flex items-center justify-center mb-4">
            <h.icon className="w-5 h-5" strokeWidth={2} />
          </div>
          <div className="font-outfit font-semibold text-white text-lg mb-1">{h.title}</div>
          <div className="text-sm text-white/70 font-manrope leading-relaxed">{h.desc}</div>
        </div>
      ))}
    </div>
  );
}


function RotatingHero() {
  const { user } = useAuth();
  // Role-scope hero messages so pros see only pro-oriented copy and companies see company-oriented copy
  const audienceMessages = React.useMemo(() => {
    if (!user) return HERO_MESSAGES;
    const role = user.role;
    if (role === "company" || role === "super_admin") {
      return HERO_MESSAGES.filter((m) => m.id === "companies");
    }
    if (role === "security_professional" || role === "lead_professional") {
      return HERO_MESSAGES.filter((m) => m.id === "guards" || m.id === "cpo" || m.id === "cluster");
    }
    return HERO_MESSAGES;
  }, [user]);

  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => {
    if (audienceMessages.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % audienceMessages.length), 7000);
    return () => clearInterval(t);
  }, [audienceMessages.length]);
  const m = audienceMessages[idx] || audienceMessages[0];
  return (
    <div data-testid="rotating-hero">
      <h1
        key={`h-${m.id}`}
        className="text-4xl sm:text-5xl lg:text-6xl font-outfit font-bold text-white tracking-tight leading-[1.05] banner-fade"
        data-testid={`hero-headline-${m.id}`}
      >
        {m.headline}
      </h1>
      <p
        key={`p-${m.id}`}
        className="text-white/85 mt-6 text-lg max-w-2xl font-manrope leading-relaxed banner-fade"
      >
        {m.body}
      </p>
      <div className="mt-4 flex items-center gap-1.5" aria-hidden="true">
        {audienceMessages.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setIdx(i)}
            className={`h-1 rounded-full transition-all ${i === idx ? "w-8 bg-[#0A0A0A]" : "w-2 bg-white/30 hover:bg-white/50"}`}
            aria-label={`Show hero message ${i + 1}`}
            data-testid={`hero-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function Home() {
  return (
    <div>
      <HomePopup />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${HERO_IMG}')` }}
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="grain" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 lg:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white px-3 py-1 rounded-full text-xs uppercase tracking-widest font-outfit font-bold mb-6 backdrop-blur" data-testid="hero-mission-badge">
              <ShieldCheck className="w-3.5 h-3.5" /> Built by Security Professionals · Designed for the Security Industry
            </div>
            <RotatingHero />
            <div className="mt-4 flex flex-wrap gap-2">
              <div className="inline-flex items-center gap-2 bg-gold-500/15 border border-gold-400/40 text-gold-300 px-3 py-1 rounded-full text-xs uppercase tracking-widest font-outfit font-bold backdrop-blur">
                <ShieldCheck className="w-3.5 h-3.5" /> Free for professionals · ERO minimum rates or above only
              </div>
              <div className="inline-flex items-center gap-2 bg-brand/20 border border-brand/50 text-white px-3 py-1 rounded-full text-xs uppercase tracking-widest font-outfit font-bold backdrop-blur" data-testid="hero-promo-2027">
                <Sparkles className="w-3.5 h-3.5" /> Free sign-up for companies until Jan 2027
              </div>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
              <Button asChild size="lg" data-testid={HOME.heroFind} className="bg-brand hover:bg-brand-hover text-white font-manrope font-semibold px-6 py-6 rounded-md">
                <Link to="/register/company">Find Security Staff <ChevronRight className="w-4 h-4 ml-1" /></Link>
              </Button>
              <Button asChild size="lg" data-testid={HOME.heroJoin} variant="outline" className="bg-[#0A0A0A] text-white hover:bg-[#141414] border-0 font-manrope font-semibold px-6 py-6">
                <Link to="/register/staff">Join as a Professional</Link>
              </Button>
              <Button asChild size="lg" data-testid={HOME.heroCompany} variant="outline" className="bg-transparent text-white border-white/40 hover:bg-white/10 font-manrope font-semibold px-6 py-6">
                <Link to="/login">Company Login</Link>
              </Button>
              <ShareOnWhatsApp message={WA_HOME_MESSAGE} variant="dark" size="lg" testId="hero-share-whatsapp" label="Share via WhatsApp" />
            </div>
          </div>
        </div>

        {/* Stat strip */}
        <div className="relative bg-navy-900/95 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-white">
            <div>
              <div className="font-outfit font-bold text-3xl">SS-0001</div>
              <div className="text-xs uppercase tracking-widest text-white/50 font-outfit">SecuSource ID Format</div>
            </div>
            <div>
              <div className="font-outfit font-bold text-3xl">100%</div>
              <div className="text-xs uppercase tracking-widest text-white/50 font-outfit">Verified Personnel</div>
            </div>
            <div>
              <div className="font-outfit font-bold text-3xl">€1<span className="text-lg">/hr</span></div>
              <div className="text-xs uppercase tracking-widest text-white/50 font-outfit">or 3% — Whichever is Lower</div>
            </div>
            <div>
              <div className="font-outfit font-bold text-3xl">iPhone-First</div>
              <div className="text-xs uppercase tracking-widest text-white/50 font-outfit">Operational Dashboard</div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission / What SecuSource Does */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <SectionHeader
              eyebrow="Our Mission"
              title="Security professionals building a better platform for security professionals."
              subtitle="We're not a tech company that discovered security. We're security operators — still working in the industry — who built a low-cost platform to fix what's broken."
            />
            <ReadMore
              className="mt-2 text-white/70 max-w-xl"
              preview="We understand the work because we still do the work."
              more="Every process — vetting, negotiation, contracts, deployment confirmation, reliability tracking — was designed by operators who know the reality of the door, the site and the principal. Not consultants who watched a documentary. We built SecuSource to raise standards, create better opportunities for professionals, and give companies a fair, low-cost way to reach the right people."
              testId="mission-readmore"
            />
            <div className="flex flex-wrap gap-2 mt-4">
              {benefits.map((b) => (
                <span key={b} className="chip"><ShieldCheck className="w-3 h-3 text-brand" /> {b}</span>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="card-flat overflow-hidden aspect-[3/4]">
              <img src={CPO_IMG} alt="Executive protection" className="w-full h-full object-cover" />
            </div>
            <div className="card-flat overflow-hidden aspect-[3/4] translate-y-8">
              <img src={EVENT_IMG} alt="Event security team" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Fair Economics band */}
      <section className="bg-navy-900 relative overflow-hidden" data-testid="fair-economics-band">
        <div className="grain" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-20">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-3">Fair Economics · Transparent by Design</div>
            <h2 className="text-3xl sm:text-4xl font-outfit font-bold text-white tracking-tight">
              Professionals keep their rate. Companies get a lower, transparent fee.
            </h2>
            <ReadMore
              className="text-slate-300 mt-4 !text-slate-300"
              preview="We built SecuSource around one principle — pros deserve fair value, companies deserve a clear low-cost way to reach them."
              more="No commissions on pro rates. No subscription fees. Companies pay one transparent introduction fee: €1/hour/worker or 3% of the booking value — whichever is lower — automatically calculated on every deployment. That's it. No surprises, no hidden fees."
              buttonMoreLabel="Show the details"
              buttonLessLabel="Hide the details"
              testId="fair-econ-readmore"
            />
          </div>

          <div className="mt-8 grid md:grid-cols-2 gap-4">
            <div className="rounded-xl p-6 bg-white/5 border border-white/10 backdrop-blur">
              <div className="text-xs uppercase tracking-widest text-gold-400 font-outfit font-bold mb-3">For Security Professionals</div>
              <ul className="space-y-3 text-white/90 font-manrope text-sm">
                <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-gold-400 flex-shrink-0 mt-0.5" /><span><strong>Free to join.</strong> Registration is completely free — no membership fees, ever.</span></li>
                <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-gold-400 flex-shrink-0 mt-0.5" /><span><strong>No commission on your rate.</strong> SecuSource never deducts anything from your hourly rate.</span></li>
                <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-gold-400 flex-shrink-0 mt-0.5" /><span><strong>You negotiate directly.</strong> Agree your rate with the hiring company for every deployment.</span></li>
                <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-gold-400 flex-shrink-0 mt-0.5" /><span><strong>Fair value.</strong> Get properly paid for your qualifications, experience and time.</span></li>
              </ul>
            </div>
            <div className="rounded-xl p-6 bg-white/5 border border-white/10 backdrop-blur">
              <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-3">For Hiring Companies</div>
              <ul className="space-y-3 text-white/90 font-manrope text-sm">
                <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" /><span><strong>One transparent introduction fee.</strong> €1 per hour per worker <em>or</em> 3% of the booking value.</span></li>
                <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" /><span><strong>Whichever is lower</strong> — automatically applied by the platform on every booking.</span></li>
                <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" /><span><strong>Verified pool.</strong> PSA licences, endorsements and role-specific ranking already in place.</span></li>
                <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" /><span><strong>No hidden costs.</strong> No subscriptions. No listing fees. You pay only when you book.</span></li>
              </ul>
            </div>
          </div>

          <div className="mt-8 text-sm text-white/50 font-manrope max-w-3xl">
            SecuSource exists to improve standards across the security industry, create better opportunities for professionals and provide greater value to companies. The fee model is intentional — it keeps our incentives aligned with the operators doing the work.
          </div>
        </div>
      </section>

      {/* How SecuSource protects professionals — Fair rates, on-time pay, video-agreed terms, SOPs, events, mutual vetting */}
      <section className="relative overflow-hidden" data-testid="pro-protection-band">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1566245024852-04fbf7842ce9?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80')` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-navy-900/85 via-navy-900/75 to-navy-800/85" aria-hidden="true" />
        <div className="grain" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-20">
          <div className="max-w-3xl mb-10">
            <div className="inline-flex items-center gap-2 bg-gold-500/20 text-gold-300 border border-gold-400/30 px-3 py-1 rounded-full text-xs font-outfit font-bold uppercase tracking-widest mb-4 backdrop-blur">
              <ShieldCheck className="w-3.5 h-3.5" /> How SecuSource protects professionals
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-outfit font-bold text-white tracking-tight leading-[1.1]">
              Fair rates. On-time payments. Terms agreed on a call — with SOPs on the table before you say yes.
            </h2>
            <ReadMore
              className="text-white/85 mt-4 !text-white/85 text-base sm:text-lg"
              preview="Security work has been broken for a long time — cash-in-hand promises, no-show payments, festival contractors treating experienced pros like disposable labour."
              more="SecuSource was built by security & CPOs to change that. Every deployment goes through the same six safeguards — ERO/SIA minimum rates enforced (no undercutting, ever), on-time payment locked in the contract, terms agreed on a video call, SOPs shared before you accept, welfare provisions baked into event bookings, and companies vetted as strictly as you are."
              buttonMoreLabel="Show the six safeguards"
              buttonLessLabel="Collapse"
              testId="pro-protect-readmore"
            />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <details className="rounded-xl p-6 bg-white/8 border border-white/15 backdrop-blur-md hover:bg-white/12 transition-colors group" data-testid="pro-protect-1">
              <summary className="list-none cursor-pointer">
                <div className="w-11 h-11 rounded-md bg-gold-500/20 text-gold-300 border border-gold-400/30 flex items-center justify-center mb-4">
                  <span className="font-outfit font-bold text-lg">€</span>
                </div>
                <div className="font-outfit font-bold text-white text-lg mb-1 flex items-center justify-between">Fair rates — you set them <ChevronRight className="w-4 h-4 group-open:rotate-90 transition-transform text-gold-300" /></div>
              </summary>
              <div className="text-sm text-slate-300 font-manrope leading-relaxed mt-2">
                No auction. No race-to-the-bottom. Your hourly rate is your hourly rate and SecuSource takes zero commission from it. The company pays the fee — never the pro.
              </div>
            </details>
            <details className="rounded-xl p-6 bg-white/8 border border-white/15 backdrop-blur-md hover:bg-white/12 transition-colors group" data-testid="pro-protect-2">
              <summary className="list-none cursor-pointer">
                <div className="w-11 h-11 rounded-md bg-gold-500/20 text-gold-300 border border-gold-400/30 flex items-center justify-center mb-4">
                  <CalendarClock className="w-5 h-5" />
                </div>
                <div className="font-outfit font-bold text-white text-lg mb-1 flex items-center justify-between">On-time payments <ChevronRight className="w-4 h-4 group-open:rotate-90 transition-transform text-gold-300" /></div>
              </summary>
              <div className="text-sm text-slate-300 font-manrope leading-relaxed mt-2">
                Payment terms are locked into the signed contract before the job. Late-paying or no-paying companies get flagged, suspended and removed — same three-strike policy we apply to guards.
              </div>
            </details>
            <details className="rounded-xl p-6 bg-white/8 border border-white/15 backdrop-blur-md hover:bg-white/12 transition-colors group" data-testid="pro-protect-3">
              <summary className="list-none cursor-pointer">
                <div className="w-11 h-11 rounded-md bg-gold-500/20 text-gold-300 border border-gold-400/30 flex items-center justify-center mb-4">
                  <Video className="w-5 h-5" />
                </div>
                <div className="font-outfit font-bold text-white text-lg mb-1 flex items-center justify-between">Agree terms on video <ChevronRight className="w-4 h-4 group-open:rotate-90 transition-transform text-gold-300" /></div>
              </summary>
              <div className="text-sm text-slate-300 font-manrope leading-relaxed mt-2">
                Every job negotiated inside a secure SecuSource video room. Agree the rate live, and the platform generates a signed PDF contract you can download and keep.
              </div>
            </details>
            <details className="rounded-xl p-6 bg-white/8 border border-white/15 backdrop-blur-md hover:bg-white/12 transition-colors group" data-testid="pro-protect-4">
              <summary className="list-none cursor-pointer">
                <div className="w-11 h-11 rounded-md bg-brand/20 text-brand border border-brand/40 flex items-center justify-center mb-4">
                  <FileTextIcon className="w-5 h-5" />
                </div>
                <div className="font-outfit font-bold text-white text-lg mb-1 flex items-center justify-between">SOPs uploaded in advance <ChevronRight className="w-4 h-4 group-open:rotate-90 transition-transform text-brand" /></div>
              </summary>
              <div className="text-sm text-slate-300 font-manrope leading-relaxed mt-2">
                Site instructions, dress code, call schedule, reporting lines — all uploaded by the company <em>before</em> you accept. No more turning up to a site with no idea what the brief is.
              </div>
            </details>
            <details className="rounded-xl p-6 bg-white/8 border border-white/15 backdrop-blur-md hover:bg-white/12 transition-colors group" data-testid="pro-protect-5">
              <summary className="list-none cursor-pointer">
                <div className="w-11 h-11 rounded-md bg-brand/20 text-brand border border-brand/40 flex items-center justify-center mb-4">
                  <SparklesIcon className="w-5 h-5" />
                </div>
                <div className="font-outfit font-bold text-white text-lg mb-1 flex items-center justify-between">Better treatment at festivals &amp; events <ChevronRight className="w-4 h-4 group-open:rotate-90 transition-transform text-brand" /></div>
              </summary>
              <div className="text-sm text-slate-300 font-manrope leading-relaxed mt-2">
                Break schedules, welfare provisions, replacement cover and end-of-shift confirmation baked into every event contract — event promoters bidding on our platform accept those terms up front.
              </div>
            </details>
            <details className="rounded-xl p-6 bg-white/8 border border-white/15 backdrop-blur-md hover:bg-white/12 transition-colors group" data-testid="pro-protect-6">
              <summary className="list-none cursor-pointer">
                <div className="w-11 h-11 rounded-md bg-brand/20 text-brand border border-brand/40 flex items-center justify-center mb-4">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div className="font-outfit font-bold text-white text-lg mb-1 flex items-center justify-between">We vet companies as strictly as we vet you <ChevronRight className="w-4 h-4 group-open:rotate-90 transition-transform text-brand" /></div>
              </summary>
              <div className="text-sm text-slate-300 font-manrope leading-relaxed mt-2">
                Same PSA/company-registration checks. Same admin approval. Same three-strike reliability policy for late-paying, no-briefing or exploitative operators. The standard cuts both ways.
              </div>
            </details>
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-gold-500 hover:bg-gold-500 text-white font-manrope font-semibold" data-testid="pro-protect-cta-join">
              <Link to="/register/staff">Join as a Professional — free <ChevronRight className="w-4 h-4 ml-1" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-manrope font-semibold" data-testid="pro-protect-cta-reliability">
              <Link to="/reliability-policy">Read the reliability policy</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Who It Helps */}
      <section className="bg-[#0A0A0A] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-20">
          <SectionHeader eyebrow="Who It Helps" title="Built for the operators who move first." />
          <WhoItHelpsGrid />
        </div>
      </section>

      {/* Anonymity Preview */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div className="order-2 lg:order-1">
            <div className="card-flat p-6 max-w-md">
              <div className="flex justify-between items-center mb-4">
                <span className="ss-id">SS-0217</span>
                <span className="verified-chip"><ShieldCheck className="w-3.5 h-3.5" /> Verified</span>
              </div>
              <div className="lock-chip mb-3"><Lock className="w-3.5 h-3.5" /> Identity hidden until booking</div>
              <div className="text-sm text-white/70 mb-2 font-manrope flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> Dublin, IE</div>
              <div className="flex flex-wrap gap-1 mb-3">
                <span className="chip">CPO / VPO</span>
                <span className="chip">Security Driver</span>
                <span className="chip">First Aid</span>
              </div>
              <div className="flex items-center gap-1 text-sm mb-2">
                {[1,2,3,4,5].map((i) => <ShieldCheck key={i} className={`w-4 h-4 ${i <= 4 ? "text-brand" : "text-white/85"}`} />)}
                <span className="text-white/60 ml-1">(28 endorsements)</span>
              </div>
              <div className="text-xs text-white/60">PSA Licence — <span className="text-gold-500 font-semibold">Valid</span></div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <SectionHeader
              eyebrow="Privacy by Design"
              title="Anonymous profiles until booking."
              subtitle="Companies see only what they need to make a decision. Real identity, phone and email are unlocked only after booking is confirmed and platform fee is paid."
            />
          </div>
        </div>
      </section>

      {/* Traditional Job Board vs SecuSource — comparison */}
      <section className="bg-[#0E0E0E] border-y border-white/10" data-testid="vs-jobboard">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 lg:py-18">
          <div className="max-w-3xl mb-8">
            <div className="inline-flex items-center gap-2 bg-brand-light text-brand border border-brand/20 px-3 py-1 rounded-full text-xs font-outfit font-bold uppercase tracking-widest mb-3">
              <Zap className="w-3.5 h-3.5" /> SecuSource is not a job board
            </div>
            <h2 className="text-3xl sm:text-4xl font-outfit font-bold text-white tracking-tight leading-tight">
              A traditional job board advertises a vacancy. SecuSource books a better professional.
            </h2>
            <p className="text-white/70 mt-3 font-manrope">
              Same industry — completely different operating model. Here&apos;s exactly what changes.
            </p>
          </div>

          {/* Desktop table */}
          <div className="hidden md:block card-flat overflow-hidden" data-testid="vs-table-desktop">
            <div className="grid grid-cols-3 border-b border-white/10 bg-[#0A0A0A]">
              <div className="p-4 font-outfit font-bold text-white/60 text-xs uppercase tracking-widest">The scenario</div>
              <div className="p-4 font-outfit font-bold text-red-700 text-sm border-l border-white/10 bg-red-50/50">
                <span className="inline-flex items-center gap-1.5"><AlertCircle className="w-4 h-4" /> Traditional job board</span>
              </div>
              <div className="p-4 font-outfit font-bold text-brand text-sm border-l border-white/10 bg-brand-light/40">
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> SecuSource</span>
              </div>
            </div>

            {[
              ["What happens", "Advertises a vacancy and waits.", "Books a better professional directly."],
              ["Sourcing model", "Waits for applications to arrive.", "Actively searches available verified talent."],
              ["Candidate profile", "Free-text CVs — nothing verified.", "Verified profiles with PSA/SIA, kit, references & endorsements."],
              ["Process", "Long recruitment cycle: apply → screen → interview → offer.", "Direct deployment platform: search → negotiate → sign → deploy."],
              ["Fees", "Recruitment agency fees (10–25% of first-year salary).", "One transparent platform fee — €1/hr/worker or 3%, whichever is lower."],
              ["Availability", "Assumes candidate is job-hunting.", "Live availability calendar — book only who's actually free."],
              ["Reliability", "No accountability after hire.", "Three-strike reliability policy + instant replacement finder."],
            ].map(([scenario, jb, ss]) => (
              <div key={scenario} className="grid grid-cols-3 border-t border-white/10 hover:bg-[#0E0E0E]/60 transition-colors">
                <div className="p-4 font-outfit font-semibold text-white text-sm">{scenario}</div>
                <div className="p-4 text-sm text-white/70 font-manrope border-l border-white/10 bg-red-50/20">
                  <span className="inline-flex items-start gap-2"><span className="text-red-500 flex-shrink-0 mt-0.5">✕</span><span>{jb}</span></span>
                </div>
                <div className="p-4 text-sm text-white font-manrope border-l border-white/10 bg-brand-light/20">
                  <span className="inline-flex items-start gap-2"><span className="text-gold-500 flex-shrink-0 mt-0.5">✓</span><span><strong>{ss}</strong></span></span>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile stacked cards */}
          <div className="md:hidden space-y-4" data-testid="vs-table-mobile">
            {[
              ["What happens", "Advertises a vacancy and waits.", "Books a better professional directly."],
              ["Sourcing model", "Waits for applications to arrive.", "Actively searches available verified talent."],
              ["Candidate profile", "Free-text CVs — nothing verified.", "Verified profiles with PSA/SIA, kit, references & endorsements."],
              ["Process", "Long recruitment cycle: apply → screen → interview → offer.", "Direct deployment platform: search → negotiate → sign → deploy."],
              ["Fees", "Recruitment agency fees (10–25% of first-year salary).", "One transparent platform fee — €1/hr/worker or 3%, whichever is lower."],
              ["Availability", "Assumes candidate is job-hunting.", "Live availability calendar — book only who's actually free."],
              ["Reliability", "No accountability after hire.", "Three-strike reliability policy + instant replacement finder."],
            ].map(([scenario, jb, ss]) => (
              <div key={scenario} className="card-flat overflow-hidden">
                <div className="p-3 bg-[#141414] font-outfit font-semibold text-white text-sm">{scenario}</div>
                <div className="p-3 bg-red-50/40 text-sm text-white/85 font-manrope border-t border-white/10">
                  <div className="text-[10px] uppercase tracking-widest text-red-700 font-outfit font-bold mb-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Traditional job board</div>
                  <span className="inline-flex items-start gap-2"><span className="text-red-500 flex-shrink-0 mt-0.5">✕</span>{jb}</span>
                </div>
                <div className="p-3 bg-brand-light/40 text-sm text-white font-manrope border-t border-white/10">
                  <div className="text-[10px] uppercase tracking-widest text-brand font-outfit font-bold mb-1 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> SecuSource</div>
                  <span className="inline-flex items-start gap-2"><span className="text-gold-500 flex-shrink-0 mt-0.5">✓</span><strong>{ss}</strong></span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-brand hover:bg-brand-hover text-white font-manrope font-semibold" data-testid="vs-cta-search">
              <Link to="/register/company">Search staff now <ChevronRight className="w-4 h-4 ml-1" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/15 text-white hover:bg-[#0E0E0E] font-manrope font-semibold" data-testid="vs-cta-join">
              <Link to="/register/staff">Join as a Professional</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Cluster Teams showcase */}
      <section className="relative overflow-hidden" data-testid="cluster-showcase">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1521737604893-d14cc237f11d?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80')` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-navy-900/94 via-navy-900/88 to-navy-800/94" aria-hidden="true" />
        <div className="grain" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-20">
          <div className="grid lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-3">
              <div className="inline-flex items-center gap-2 bg-gold-500/20 text-gold-300 border border-gold-400/30 px-3 py-1 rounded-full text-xs font-outfit font-bold uppercase tracking-widest mb-4 backdrop-blur">
                <Users2 className="w-3.5 h-3.5" /> Cluster Teams · SecuSource&apos;s edge
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-outfit font-bold text-white tracking-tight leading-[1.1]">
                Need more than one person? Deploy a complete security team in minutes.
              </h2>
              <ReadMore
                className="mt-4 !text-white/85 text-base sm:text-lg"
                preview="Deploy complete security teams or individual professionals in minutes — not weeks."
                more={[
                  "\u201CWork with professionals you know and trust to build stronger teams, deliver better operations, and grow your reputation together.\u201D",
                  "Assemble a complete team based on the skills your operation requires — professionals who have worked together, done well together, and are rated and verified as a unit. One booking, one contract, one deployment.",
                ]}
                buttonMoreLabel="Read the vision"
                buttonLessLabel="Collapse"
                testId="cluster-readmore"
              />

              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" className="bg-gold-500 hover:bg-gold-500 text-white font-manrope font-semibold" data-testid="cluster-cta-browse">
                  <Link to="/cluster-teams">Browse cluster teams <ChevronRight className="w-4 h-4 ml-1" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-manrope font-semibold" data-testid="cluster-cta-form">
                  <Link to="/register/staff">Lead a team — join SecuSource</Link>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-2 grid grid-cols-2 gap-3" data-testid="cluster-benefits-summary">
              <div className="rounded-xl p-5 bg-white/8 border border-white/15 backdrop-blur-md">
                <Users2 className="w-6 h-6 text-gold-300 mb-2" />
                <div className="font-outfit font-bold text-white text-base">Trusted together</div>
              </div>
              <div className="rounded-xl p-5 bg-white/8 border border-white/15 backdrop-blur-md">
                <ShieldCheck className="w-6 h-6 text-gold-300 mb-2" />
                <div className="font-outfit font-bold text-white text-base">Rated as a unit</div>
              </div>
              <div className="rounded-xl p-5 bg-white/8 border border-white/15 backdrop-blur-md">
                <SparklesIcon className="w-6 h-6 text-brand mb-2" />
                <div className="font-outfit font-bold text-white text-base">Kit combined</div>
              </div>
              <div className="rounded-xl p-5 bg-white/8 border border-white/15 backdrop-blur-md">
                <Zap className="w-6 h-6 text-brand mb-2" />
                <div className="font-outfit font-bold text-white text-base">Deployed in minutes</div>
              </div>
              <details className="col-span-2 rounded-xl p-4 bg-white/5 border border-white/15 backdrop-blur-md group cursor-pointer" data-testid="cluster-benefits-more">
                <summary className="list-none inline-flex items-center gap-2 text-gold-300 font-outfit font-bold uppercase tracking-widest text-xs">
                  <ChevronRight className="w-4 h-4 group-open:rotate-90 transition-transform" />
                  Read the full benefits
                </summary>
                <div className="mt-3 space-y-2 text-sm text-slate-300 font-manrope">
                  <div><strong className="text-white">Trusted together:</strong> pros who&apos;ve already worked shifts together — chemistry proven on the ground.</div>
                  <div><strong className="text-white">Rated as a unit:</strong> team rating &amp; endorsements — not just individuals.</div>
                  <div><strong className="text-white">Kit combined:</strong> vehicles, medical, radios &amp; PPE stacked across the team.</div>
                  <div><strong className="text-white">Deployed in minutes:</strong> one booking, one contract, one team — search → sign → deploy.</div>
                </div>
              </details>
            </div>
          </div>
        </div>
      </section>

      {/* Solo pros — not in a cluster team yet */}
      <section className="bg-[#0A0A0A] border-b border-white/10" data-testid="solo-pros-band">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 lg:py-18">
          <div className="grid lg:grid-cols-5 gap-8 items-center">
            <div className="lg:col-span-3">
              <div className="inline-flex items-center gap-2 bg-gold-50 text-gold-600 border border-gold-200 px-3 py-1 rounded-full text-xs font-outfit font-bold uppercase tracking-widest mb-3">
                <Users2 className="w-3.5 h-3.5" /> Not in a cluster team yet?
              </div>
              <h2 className="text-3xl sm:text-4xl font-outfit font-bold text-white tracking-tight leading-tight">
                Work with talented individuals from the SecuSource network. Grow into a team.
              </h2>
              <ReadMore
                className="mt-4 text-base sm:text-lg"
                preview="Solo pro today, part of a cluster team tomorrow."
                more={[
                  "Every verified pro on SecuSource is discoverable by team leads looking to build. Take the individual bookings that come in, deliver great work, collect endorsements — and cluster invitations start arriving.",
                  "Become part of cluster teams for fast deployment with good colleagues you already know and trust. Excel in what you do — the endorsements, reliability score and repeat bookings do the networking for you.",
                ]}
                buttonMoreLabel="How the journey works"
                testId="solo-pro-readmore"
              />

              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild size="lg" className="bg-gold-500 hover:bg-gold-600 text-white font-manrope font-semibold" data-testid="solo-cta-join">
                  <Link to="/register/staff">Start solo — join free <ChevronRight className="w-4 h-4 ml-1" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-white/15 text-white hover:bg-[#0E0E0E] font-manrope font-semibold" data-testid="solo-cta-teams">
                  <Link to="/cluster-teams">See existing teams</Link>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-2 space-y-3" data-testid="solo-pros-steps">
              <div className="card-flat p-4 flex items-start gap-3 border-l-4 border-l-gold-500">
                <div className="w-8 h-8 rounded-full bg-gold-100 text-gold-600 flex items-center justify-center font-outfit font-bold flex-shrink-0">1</div>
                <div>
                  <div className="font-outfit font-semibold text-white">Join solo — free</div>
                  <div className="text-sm text-white/70 font-manrope">Verified profile visible to every hiring company on the platform.</div>
                </div>
              </div>
              <div className="card-flat p-4 flex items-start gap-3 border-l-4 border-l-gold-500">
                <div className="w-8 h-8 rounded-full bg-gold-100 text-gold-600 flex items-center justify-center font-outfit font-bold flex-shrink-0">2</div>
                <div>
                  <div className="font-outfit font-semibold text-white">Excel &amp; earn endorsements</div>
                  <div className="text-sm text-white/70 font-manrope">Deliver great work → clients endorse you → ranking climbs automatically.</div>
                </div>
              </div>
              <div className="card-flat p-4 flex items-start gap-3 border-l-4 border-l-brand">
                <div className="w-8 h-8 rounded-full bg-brand-light text-brand flex items-center justify-center font-outfit font-bold flex-shrink-0">3</div>
                <div>
                  <div className="font-outfit font-semibold text-white">Get invited to cluster teams</div>
                  <div className="text-sm text-white/70 font-manrope">Lead Pros spot high-performing solos in search and invite them to teams.</div>
                </div>
              </div>
              <div className="card-flat p-4 flex items-start gap-3 border-l-4 border-l-brand">
                <div className="w-8 h-8 rounded-full bg-brand-light text-brand flex items-center justify-center font-outfit font-bold flex-shrink-0">4</div>
                <div>
                  <div className="font-outfit font-semibold text-white">Deploy fast with colleagues you trust</div>
                  <div className="text-sm text-white/70 font-manrope">One team, one booking, one contract — with people you already know.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Meritocracy banner — visibility is earned */}
      <section className="relative overflow-hidden bg-navy-900" data-testid="meritocracy-banner">
        <div className="grain" />
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-brand/10 blur-3xl" aria-hidden="true" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-gold-500/10 blur-3xl" aria-hidden="true" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 lg:py-24">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-brand/15 text-brand border border-brand/30 px-3 py-1 rounded-full text-xs font-outfit font-bold uppercase tracking-widest mb-5 backdrop-blur">
              <Sparkles className="w-3.5 h-3.5" /> Meritocracy · No paid promotion
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-outfit font-bold text-white leading-[1.1] tracking-tight">
              Your visibility is earned through the value you bring to the network.
            </h2>
            <p className="text-slate-300 mt-5 font-manrope text-base sm:text-lg leading-relaxed">
              SecuSource ranks you on merit, not marketing spend. Every one of these builds your reputation on the platform.
            </p>
          </div>

          {/* Floating pills — 3D-ish scattered layout */}
          <div className="relative mt-14" data-testid="meritocracy-pills">
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
              {[
                { label: "Positive client reviews", delay: "floaty-d1", tone: "emerald" },
                { label: "Trusted referrals (approved)", delay: "floaty-d2", tone: "brand" },
                { label: "Building successful cluster teams", delay: "floaty-d3", tone: "emerald" },
                { label: "Reliability & attendance", delay: "floaty-d4", tone: "brand" },
                { label: "Verified qualifications", delay: "floaty-d5", tone: "emerald" },
                { label: "Repeat bookings from clients", delay: "floaty-d6", tone: "brand" },
              ].map((p, i) => (
                <div
                  key={p.label}
                  className={`floaty ${p.delay} rounded-full backdrop-blur-md border px-5 py-3 font-outfit font-semibold text-sm sm:text-base flex items-center gap-2 ${
                    p.tone === "emerald"
                      ? "bg-gold-500/15 border-gold-400/30 text-gold-100"
                      : "bg-brand/15 border-brand/40 text-white"
                  }`}
                  data-testid={`merit-pill-${i}`}
                >
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${p.tone === "emerald" ? "bg-gold-400" : "bg-brand"}`} aria-hidden="true" />
                  {p.label}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-14 text-center">
            <p className="text-white/50 font-manrope text-sm">
              Do the work. Show up. Deliver. The algorithm does the rest.
            </p>
          </div>
        </div>
      </section>

      {/* Get known faster — career acceleration for pros */}
      <section className="bg-[#0A0A0A]" data-testid="get-known-faster">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-gold-50 text-gold-600 border border-gold-200 px-3 py-1 rounded-full text-xs font-outfit font-bold uppercase tracking-widest mb-4">
                <Zap className="w-3.5 h-3.5" /> Career acceleration
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-outfit font-bold text-white tracking-tight leading-[1.05]">
                Get known in the industry — faster.
              </h2>
              <ReadMore
                className="mt-5 text-base sm:text-lg"
                preview="Traditional security work runs on who-you-know. SecuSource is the shortcut."
                more="Every verified booking, endorsement and cluster team invite compounds your visibility — so opportunities start finding you."
                buttonMoreLabel="Why it works"
                buttonLessLabel="Collapse"
                testId="known-intro-readmore"
              />

              <details className="mt-8 group" data-testid="known-steps-details">
                <summary className="list-none cursor-pointer inline-flex items-center gap-2 text-brand font-outfit font-bold uppercase tracking-widest text-xs">
                  <ChevronRight className="w-4 h-4 group-open:rotate-90 transition-transform" />
                  See the 4 ways your reputation compounds
                </summary>
                <div className="mt-4 space-y-4">
                <div className="flex items-start gap-3" data-testid="known-1">
                  <div className="w-9 h-9 rounded-md bg-gold-100 text-gold-600 flex items-center justify-center flex-shrink-0 font-outfit font-bold">1</div>
                  <div>
                    <div className="font-outfit font-semibold text-white">Every company on the platform sees your profile</div>
                    <div className="text-sm text-white/70 font-manrope">No agency gatekeeping. Verified pros appear directly in every search — from your first day on the platform.</div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-testid="known-2">
                  <div className="w-9 h-9 rounded-md bg-gold-100 text-gold-600 flex items-center justify-center flex-shrink-0 font-outfit font-bold">2</div>
                  <div>
                    <div className="font-outfit font-semibold text-white">Every completed job compounds your ranking</div>
                    <div className="text-sm text-white/70 font-manrope">Client endorsements + reliability + repeat bookings all feed the algorithm. Reputation builds automatically.</div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-testid="known-3">
                  <div className="w-9 h-9 rounded-md bg-gold-100 text-gold-600 flex items-center justify-center flex-shrink-0 font-outfit font-bold">3</div>
                  <div>
                    <div className="font-outfit font-semibold text-white">Cluster teams expand your network overnight</div>
                    <div className="text-sm text-white/70 font-manrope">Join a team → get seen by clients who wouldn&apos;t normally book solo pros. Small role today, lead role tomorrow.</div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-testid="known-4">
                  <div className="w-9 h-9 rounded-md bg-gold-100 text-gold-600 flex items-center justify-center flex-shrink-0 font-outfit font-bold">4</div>
                  <div>
                    <div className="font-outfit font-semibold text-white">Opportunities start finding you</div>
                    <div className="text-sm text-white/70 font-manrope">Once your ranking climbs, companies book you directly — no more chasing job posts.</div>
                  </div>
                </div>
              </div>
              </details>

              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" className="bg-gold-500 hover:bg-gold-600 text-white font-manrope font-semibold" data-testid="known-cta-join">
                  <Link to="/register/staff">Start building your reputation <ChevronRight className="w-4 h-4 ml-1" /></Link>
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4" data-testid="known-stats">
              <div className="card-flat p-6 bg-gradient-to-br from-gold-50 to-[#0A0A0A] border-l-4 border-l-gold-500">
                <div className="text-4xl font-outfit font-bold text-gold-600">10×</div>
                <div className="text-sm font-outfit font-semibold text-white mt-2">More visibility</div>
                <div className="text-xs text-white/60 font-manrope mt-1">vs. traditional job-board CV</div>
              </div>
              <div className="card-flat p-6 bg-gradient-to-br from-brand-light to-[#0A0A0A] border-l-4 border-l-brand">
                <div className="text-4xl font-outfit font-bold text-brand">0</div>
                <div className="text-sm font-outfit font-semibold text-white mt-2">Gatekeepers</div>
                <div className="text-xs text-white/60 font-manrope mt-1">No agency between you and the client</div>
              </div>
              <div className="card-flat p-6 bg-gradient-to-br from-gold-50 to-[#0A0A0A] border-l-4 border-l-gold-500">
                <div className="text-4xl font-outfit font-bold text-gold-600">24h</div>
                <div className="text-sm font-outfit font-semibold text-white mt-2">Time to visibility</div>
                <div className="text-xs text-white/60 font-manrope mt-1">Once your profile is approved, you&apos;re live</div>
              </div>
              <div className="card-flat p-6 bg-gradient-to-br from-brand-light to-[#0A0A0A] border-l-4 border-l-brand">
                <div className="text-4xl font-outfit font-bold text-brand">∞</div>
                <div className="text-sm font-outfit font-semibold text-white mt-2">Endorsements</div>
                <div className="text-xs text-white/60 font-manrope mt-1">Every job compounds your reputation</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cover & Reliability — the replacement system */}
      <section className="bg-[#0A0A0A] border-y border-white/10" data-testid="cover-reliability">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 lg:py-18">
          <div className="max-w-3xl mb-8">
            <div className="inline-flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 px-3 py-1 rounded-full text-xs font-outfit font-bold uppercase tracking-widest mb-3">
              <AlertCircle className="w-3.5 h-3.5" /> Cover &amp; Reliability
            </div>
            <h2 className="text-3xl sm:text-4xl font-outfit font-bold text-white leading-tight">
              If someone can&apos;t turn up, we find a replacement before the gap becomes your problem.
            </h2>
            <p className="text-white/70 mt-3 font-manrope">
              SecuSource has a live no-show / cover engine — reported once, actioned immediately, backed by our three-strike policy.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="card-flat p-5 border-l-4 border-l-red-500" data-testid="cover-1">
              <div className="text-xs uppercase tracking-widest text-red-600 font-outfit font-bold mb-1">Step 1 — Report</div>
              <div className="font-outfit font-bold text-white text-lg mb-1">One-tap no-show report</div>
              <div className="text-sm text-white/70 font-manrope leading-relaxed">
                Company reports the no-show inside the booking. Admin is alerted instantly. A three-strike counter starts on the pro&apos;s record.
              </div>
            </div>
            <div className="card-flat p-5 border-l-4 border-l-amber-500" data-testid="cover-2">
              <div className="text-xs uppercase tracking-widest text-amber-600 font-outfit font-bold mb-1">Step 2 — Suggest</div>
              <div className="font-outfit font-bold text-white text-lg mb-1">Instant replacement finder</div>
              <div className="text-sm text-white/70 font-manrope leading-relaxed">
                The platform surfaces every verified pro nearby with the right qualification, kit and availability — ranked by match score. One click to open a new negotiation.
              </div>
            </div>
            <div className="card-flat p-5 border-l-4 border-l-gold-500" data-testid="cover-3">
              <div className="text-xs uppercase tracking-widest text-gold-500 font-outfit font-bold mb-1">Step 3 — Protect</div>
              <div className="font-outfit font-bold text-white text-lg mb-1">Three-strike protection</div>
              <div className="text-sm text-white/70 font-manrope leading-relaxed">
                Pros with three no-shows are suspended. Reliable pros get ranking boosts. Companies see the reliability score before they book.
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-brand hover:bg-brand-hover text-black font-manrope font-semibold" data-testid="cover-cta-policy">
              <Link to="/reliability-policy">Read the reliability policy</Link>
            </Button>
            <details className="card-flat p-4 flex-1 min-w-[280px] cursor-pointer group" data-testid="cover-details">
              <summary className="list-none flex items-center justify-between gap-2 font-manrope font-semibold text-white">
                <span>How does the replacement finder work?</span>
                <ChevronRight className="w-4 h-4 group-open:rotate-90 transition-transform text-brand" />
              </summary>
              <div className="text-sm text-white/70 mt-3 font-manrope leading-relaxed">
                When a company reports a no-show, our algorithm scans every verified pro in the region (or willing to travel) who holds the required qualification, kit and availability for the shift. Matches are ranked by proximity, rating, reliability score and previous work with that company. Contact-unlock terms are the same as a normal booking — the replacement pro is protected by the same contract, video-agreed rate and payment guarantee.
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* Elite Standards — why our pros are different */}
      <section className="bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white" data-testid="elite-standards">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-gold-500/20 text-gold-300 border border-gold-400/30 px-3 py-1 rounded-full text-xs font-outfit font-bold uppercase tracking-widest mb-4">
              <ShieldCheck className="w-3.5 h-3.5" /> Why SecuSource pros are elite
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-outfit font-bold leading-[1.05] tracking-tight text-white">
              Every professional on the platform clears five independent gates.
            </h2>
            <p className="text-slate-300 mt-4 font-manrope text-base sm:text-lg">
              Not a marketplace. Not a jobs board. A vetted workforce built for clients who cannot afford to book the wrong person.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3 mt-10">
            <div className="rounded-xl bg-white/5 border border-white/10 p-5" data-testid="standard-experience">
              <div className="text-brand text-3xl font-outfit font-bold">2yrs+</div>
              <div className="text-xs uppercase tracking-widest text-gold-300 font-outfit font-bold mt-2 mb-1">Minimum experience</div>
              <div className="text-sm text-slate-300 font-manrope">No entry-level guards. Verified security industry experience required before a profile ever appears in search.</div>
            </div>
            <div className="rounded-xl bg-white/5 border border-white/10 p-5" data-testid="standard-licence">
              <div className="text-brand text-3xl font-outfit font-bold">PSA / SIA</div>
              <div className="text-xs uppercase tracking-widest text-gold-300 font-outfit font-bold mt-2 mb-1">Licence on file</div>
              <div className="text-sm text-slate-300 font-manrope">Every pro submits their PSA (Ireland) or SIA (UK) licence number and expiry — verifiable via our public badge lookup.</div>
            </div>
            <div className="rounded-xl bg-white/5 border border-white/10 p-5" data-testid="standard-references">
              <div className="text-brand text-3xl font-outfit font-bold">Refs ✓</div>
              <div className="text-xs uppercase tracking-widest text-gold-300 font-outfit font-bold mt-2 mb-1">Employer references</div>
              <div className="text-sm text-slate-300 font-manrope">A previous employer confirms reliability, timekeeping, conduct and integrity — by direct email reply, not a checkbox.</div>
            </div>
            <div className="rounded-xl bg-white/5 border border-white/10 p-5" data-testid="standard-english">
              <div className="text-brand text-3xl font-outfit font-bold">B2+</div>
              <div className="text-xs uppercase tracking-widest text-gold-300 font-outfit font-bold mt-2 mb-1">English level</div>
              <div className="text-sm text-slate-300 font-manrope">Minimum CEFR B2 — reads SOPs, briefings and radio comms without ambiguity. Non-negotiable.</div>
            </div>
            <div className="rounded-xl bg-white/5 border border-white/10 p-5" data-testid="standard-reliability">
              <div className="text-brand text-3xl font-outfit font-bold">3-strike</div>
              <div className="text-xs uppercase tracking-widest text-gold-300 font-outfit font-bold mt-2 mb-1">Reliability policy</div>
              <div className="text-sm text-slate-300 font-manrope">Enforced automatically by the platform. One no-show = warning. Three = suspended pending appeal.</div>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-brand hover:bg-brand-hover text-white font-manrope font-semibold" data-testid="elite-cta-reliability">
              <Link to="/reliability-policy">Read the reliability policy</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-manrope font-semibold" data-testid="elite-cta-verify">
              <Link to="/verify-badge">Verify a licence</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-manrope font-semibold" data-testid="elite-cta-trust">
              <Link to="/trust">See live platform stats</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Sign-up walkthrough — How SecuSource works */}
      <section className="bg-[#0A0A0A] border-y border-white/10" data-testid="signup-walkthrough">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-20">
          <SectionHeader
            eyebrow="How to Sign Up"
            title="Three ways to join. All free to register."
            subtitle="Pick your path below — every account starts with a free registration and moves to admin verification within one working day."
          />

          <div className="grid md:grid-cols-3 gap-4 mt-2">
            {/* Path 1 — Professional */}
            <div className="card-flat p-6 border-l-4 border-l-brand flex flex-col" data-testid="signup-path-pro">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-10 rounded-md bg-brand text-white flex items-center justify-center"><UserPlus className="w-5 h-5" strokeWidth={2.2} /></div>
                <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold">Path 1 · Professional</div>
              </div>
              <div className="font-outfit font-bold text-white text-lg mb-1">Guards · CPOs · Drivers · Supervisors</div>
              <div className="inline-flex w-fit items-center gap-1 bg-gold-50 text-gold-600 border border-gold-200 px-2 py-0.5 rounded-full text-[10px] font-outfit font-bold uppercase tracking-widest mb-3">€0 · Free forever</div>
              <ol className="space-y-2 text-sm text-white/70 font-manrope flex-1">
                <li><strong className="text-white">1.</strong> Register with your PSA / SIA number, phone, county and role categories.</li>
                <li><strong className="text-white">2.</strong> Upload your licence and qualification documents (private — admin only).</li>
                <li><strong className="text-white">3.</strong> Admin verifies you within 1 working day. You get a SecuSource ID (e.g. SS-0142).</li>
                <li><strong className="text-white">4.</strong> Set your availability, get discovered by companies, negotiate your own rate.</li>
              </ol>
              <Button asChild className="mt-5 bg-brand hover:bg-brand-hover text-white font-manrope font-semibold" data-testid="signup-pro-cta">
                <Link to="/register/staff">Register as a Professional <ChevronRight className="w-4 h-4 ml-1" /></Link>
              </Button>
            </div>

            {/* Path 2 — Lead Pro / Cluster */}
            <div className="card-flat p-6 border-l-4 border-l-gold-500 flex flex-col" data-testid="signup-path-lead">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-10 rounded-md bg-gold-500 text-white flex items-center justify-center"><Users2 className="w-5 h-5" strokeWidth={2.2} /></div>
                <div className="text-xs uppercase tracking-widest text-gold-500 font-outfit font-bold">Path 2 · Lead a Cluster</div>
              </div>
              <div className="font-outfit font-bold text-white text-lg mb-1">Lead Pros · Team Leads · Supervisors</div>
              <div className="inline-flex w-fit items-center gap-1 bg-gold-50 text-gold-600 border border-gold-200 px-2 py-0.5 rounded-full text-[10px] font-outfit font-bold uppercase tracking-widest mb-3">€0 · Free forever</div>
              <ol className="space-y-2 text-sm text-white/70 font-manrope flex-1">
                <li><strong className="text-white">1.</strong> Complete step 1–3 as a Professional (above).</li>
                <li><strong className="text-white">2.</strong> From your dashboard tap <em>My Team</em> and create a cluster.</li>
                <li><strong className="text-white">3.</strong> Invite verified colleagues by their SecuSource ID.</li>
                <li><strong className="text-white">4.</strong> Submit combined availability and get booked as a full unit — kit, comms, medical.</li>
              </ol>
              <Button asChild variant="outline" className="mt-5 border-gold-500 text-gold-500 hover:bg-gold-50 font-manrope font-semibold" data-testid="signup-lead-cta">
                <Link to="/cluster-teams">Learn about cluster teams <ChevronRight className="w-4 h-4 ml-1" /></Link>
              </Button>
            </div>

            {/* Path 3 — Company */}
            <div className="rounded-xl bg-navy-900 text-white p-6 flex flex-col" data-testid="signup-path-company">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-10 rounded-md bg-[#0A0A0A] text-white flex items-center justify-center"><Building2 className="w-5 h-5" strokeWidth={2.2} /></div>
                <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold">Path 3 · Company</div>
              </div>
              <div className="font-outfit font-bold text-white text-lg mb-1">Security firms · Event organisers · Clients</div>
              <div className="inline-flex w-fit items-center gap-1 bg-white/10 border border-white/20 text-white/85 px-2 py-0.5 rounded-full text-[10px] font-outfit font-bold uppercase tracking-widest mb-3">€0 to register · Pay only when you book</div>
              <ol className="space-y-2 text-sm text-slate-300 font-manrope flex-1">
                <li><strong className="text-white">1.</strong> Register your business — name, contact, location, company type.</li>
                <li><strong className="text-white">2.</strong> Admin approves you within 1 working day. You get a SecuSource ID (e.g. SC-0021).</li>
                <li><strong className="text-white">3.</strong> Search vetted pros and cluster teams by county, licence, role.</li>
                <li><strong className="text-white">4.</strong> Request a booking. See the fee before confirming. Contacts unlock on approval.</li>
              </ol>
              <Button asChild className="mt-5 bg-brand hover:bg-brand-hover text-white font-manrope font-semibold" data-testid="signup-company-cta">
                <Link to="/register/company">Register as a Company <ChevronRight className="w-4 h-4 ml-1" /></Link>
              </Button>
            </div>
          </div>

          {/* Footnotes */}
          <div className="mt-8 grid sm:grid-cols-3 gap-4 text-sm text-white/70 font-manrope">
            <div className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-gold-500 mt-0.5 flex-shrink-0" /><span><strong className="text-white">Registration is free</strong> for every role — no subscription, no listing fee.</span></div>
            <div className="flex items-start gap-2"><Lock className="w-4 h-4 text-brand mt-0.5 flex-shrink-0" /><span><strong className="text-white">Documents stay private</strong> — only admin sees PSA / SIA files. Companies see status only.</span></div>
            <div className="flex items-start gap-2"><CalendarClock className="w-4 h-4 text-white mt-0.5 flex-shrink-0" /><span><strong className="text-white">Approval within 1 working day</strong> — usually much faster. Watch your inbox.</span></div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy-900 relative overflow-hidden">
        <div className="grain" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 relative">
          <div className="text-center max-w-3xl mx-auto">
            <div className="text-xs uppercase tracking-widest text-white/50 font-outfit font-bold mb-3">Get Started</div>
            <h2 className="text-3xl sm:text-4xl font-outfit font-bold text-white tracking-tight">
              Built for the industry. By the industry.
            </h2>
            <p className="text-slate-300 mt-3 font-manrope">
              Onboard free of charge. Set your own rate. Book verified professionals with one transparent fee.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
              <Button asChild size="lg" data-testid={HOME.ctaRegisterStaff} className="bg-brand hover:bg-brand-hover text-white font-manrope px-6 py-6">
                <Link to="/register/staff">Register as Staff</Link>
              </Button>
              <Button asChild size="lg" data-testid={HOME.ctaRegisterCompany} variant="outline" className="bg-[#0A0A0A] text-white border-0 hover:bg-[#141414] font-manrope px-6 py-6">
                <Link to="/register/company">Register as Company</Link>
              </Button>
              <Button asChild size="lg" data-testid={HOME.ctaRequestDemo} variant="outline" className="bg-transparent text-white border-white/40 hover:bg-white/10 font-manrope px-6 py-6">
                <Link to="/contact">Request a Demo</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
